import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { ButterCanvas } from '@/components/ButterCanvas';
import { Marquee } from '@/components/Marquee';
import { MaskLine, FadeUp, EASE } from '@/components/Reveal';
import { WorkCard } from '@/components/WorkCard';
import { CaseModal } from '@/components/CaseModal';
import { SERVICES, WORK, IMAGES } from '@/data/content';

const FEATURED = [...WORK.slice(0, 3), WORK.find((w) => w.id === 'work-11')];

const TICKER = ['Strategy', 'Content', 'Production', 'Design', 'Management', 'Smooth. Rich. Confident.'];

const STATS = [
  { value: '17+', label: 'Brands in the family' },
  { value: '360°', label: 'Content solutions' },
  { value: 'EN/AR', label: 'Bilingual, natively' },
  { value: '5-Step', label: 'Proven process' },
];

export default function Home() {
  const [active, setActive] = useState(null);
  const { scrollY } = useScroll();
  const canvasY = useTransform(scrollY, [0, 800], [0, 140]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden px-6 sm:px-12 pt-32 pb-20">
        <motion.div style={{ y: canvasY }} className="absolute inset-y-0 right-0 w-full lg:w-[58%] pointer-events-none">
          <ButterCanvas className="w-full h-full" />
        </motion.div>

        <div className="relative z-10 max-w-[1500px]">
          <motion.div
            data-testid="hero-location-pill"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2.5 border border-ink rounded-full px-5 py-2 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/70"
          >
            <span className="w-2 h-2 rounded-full bg-royal" />
            Dubai — 360° Content Studio
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-slab font-black text-ink leading-[0.96] tracking-tight text-[13.5vw] sm:text-[11vw] lg:text-[8.2vw] mt-8"
          >
            <MaskLine delay={0.2}>WE MAKE</MaskLine>
            <span className="relative block">
              <MaskLine delay={0.34}>CONTENT</MaskLine>
              <motion.span
                initial={{ opacity: 0, y: 24, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -4 }}
                transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
                className="font-script text-royal absolute -top-[0.58em] right-[6%] text-[0.92em] pointer-events-none select-none"
              >
                smooth
              </motion.span>
            </span>
            <MaskLine delay={0.48}>THAT STICKS.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
            className="mt-9 max-w-xl text-base sm:text-lg leading-relaxed text-ink/75"
          >
            We’re Butter Media — a bunch of passionate storytellers and strategists. Everyone has a story
            to tell, and we want to tell yours in the best way possible: messages that resonate, engage,
            and drive results.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/work"
              data-testid="hero-cta-explore-work"
              className="inline-flex group items-center gap-2.5 bg-ink text-cream font-slab font-bold px-8 py-4 rounded-full hover:bg-royal transition-colors duration-300"
            >
              See Selected Work
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex group items-center gap-2.5 bg-butter text-ink font-slab font-bold px-8 py-4 rounded-full border border-ink shadow-[4px_4px_0_#1A1A1A] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all duration-300"
            >
              Start a Project
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={TICKER}
        testid="home-marquee"
        duration={32}
        className="bg-butter border-y border-ink py-4 font-slab font-bold uppercase tracking-wider text-xl sm:text-2xl text-ink"
      />

      {/* WHO WE ARE */}
      <section data-testid="about-section" className="px-6 sm:px-12 py-28 sm:py-36">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-[1500px] mx-auto">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-6">01 — Who We Are</p>
              <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Storytellers on a mission.
              </h2>
              <p className="mt-7 text-base sm:text-lg leading-relaxed text-ink/75">
                At Butter Media, we are a bunch of passionate storytellers and strategists with a mission
                to enhance your brand through powerful, focused storytelling.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/75">
                With decades of combined experience in the realm of content, we craft messages that
                resonate, engage, and drive results.
              </p>
              <Link
                to="/services"
                data-testid="about-cta-services"
                className="mt-9 inline-flex group items-center gap-2 font-slab font-bold text-royal"
              >
                Explore the 360° spread
                <ArrowRight size={18} className="group-hover:translate-x-1.5 transition-transform duration-300" />
              </Link>
            </FadeUp>
          </div>

          <div className="lg:col-span-7 relative">
            <FadeUp delay={0.15}>
              <div className="relative clip-corner-tl overflow-hidden border border-ink">
                <img src={IMAGES.crew} alt="Butter Media crew on set" className="w-full aspect-[4/3] object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-3 sm:-left-8 bg-butter border border-ink rounded-2xl px-7 py-5 rotate-[-3deg] shadow-[5px_5px_0_#1A1A1A]">
                <p className="font-script text-3xl text-royal leading-none">decades</p>
                <p className="font-slab font-bold text-sm mt-1.5 uppercase tracking-wide">of combined experience</p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SERVICES INDEX */}
      <section data-testid="services-overview" className="py-24 sm:py-32 border-t border-ink/10">
        <div className="px-6 sm:px-12 max-w-[1500px] mx-auto mb-14 flex flex-wrap items-end justify-between gap-6">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-5">02 — What We Do</p>
            <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              The 360° spread.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="max-w-sm text-ink/65 leading-relaxed">
              High-impact content across multiple formats — in English and Arabic.
            </p>
          </FadeUp>
        </div>

        <div className="border-b border-ink/15">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.num} delay={i * 0.05}>
              <Link
                to="/services"
                data-testid={`service-row-${s.num}`}
                className="group grid grid-cols-12 items-center gap-4 px-6 sm:px-12 py-7 sm:py-9 border-t border-ink/15 hover:bg-butter transition-colors duration-300"
              >
                <span className="col-span-2 sm:col-span-1 font-slab font-black text-royal text-lg sm:text-xl">{s.num}</span>
                <span className="col-span-10 sm:col-span-5 font-slab font-bold text-2xl sm:text-4xl tracking-tight group-hover:translate-x-2 transition-transform duration-300">
                  {s.title}
                </span>
                <span className="hidden md:block col-span-5 text-sm text-ink/55">{s.tagline}</span>
                <span className="hidden sm:flex col-span-1 justify-end">
                  <ArrowUpRight size={26} className="opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SELECTED WORK PREVIEW */}
      <section data-testid="work-preview" className="mx-3 sm:mx-6 my-6 bg-ink text-cream rounded-[2.5rem] px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <FadeUp>
              <p className="text-[11px] uppercase tracking-[0.3em] text-butter font-bold mb-5">03 — Selected Work</p>
              <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Fresh from<br />the studio.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Link
                to="/work"
                data-testid="work-preview-cta"
                className="inline-flex group items-center gap-2.5 border border-cream/30 rounded-full px-7 py-3.5 font-slab font-bold hover:bg-butter hover:text-ink hover:border-butter transition-colors duration-300"
              >
                View all work
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </FadeUp>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURED.map((w, i) => (
              <FadeUp key={w.id} delay={i * 0.1}>
                <WorkCard work={w} onOpen={setActive} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-strip" className="px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
          {STATS.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.08}>
              <p className="font-slab font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight">
                {s.value}
              </p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-ink/55 font-medium">{s.label}</p>
              <span className="block w-10 h-1 bg-royal mt-5" />
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="px-6 sm:px-12 pb-8">
        <FadeUp className="max-w-[1500px] mx-auto">
          <Link
            to="/contact"
            data-testid="home-cta-banner"
            className="group block bg-butter border border-ink rounded-[2rem] px-8 py-16 sm:p-24 text-center shadow-[8px_8px_0_#1A1A1A] hover:shadow-none hover:translate-x-2 hover:translate-y-2 transition-all duration-300"
          >
            <p className="font-script text-royal text-4xl sm:text-5xl mb-4 -rotate-2 inline-block">let’s collaborate</p>
            <h2 className="font-slab font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight">
              Got a story?<br />Let’s make it stick.
            </h2>
            <span className="mt-10 inline-flex items-center gap-3 font-slab font-bold text-lg border-b-2 border-ink pb-1">
              Start the conversation
              <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform duration-300" />
            </span>
          </Link>
        </FadeUp>
      </section>

      <CaseModal work={active} onClose={() => setActive(null)} />
    </main>
  );
}
